import type { TaskPerson } from '@obras/shared';
import { formatDate } from '@obras/shared';
import { Plus, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import { EASE_OUT_QUINT } from '../../components/motion.js';
import { CONTROL_BASE, CONTROL_TONE } from '../../components/ui/Field.js';
import { SpringCheck } from '../../components/ui/SpringCheck.js';
import { cn } from '../../lib/cn.js';

export interface ItemDoChecklist {
  readonly id: string;
  readonly content: string;
  readonly isDone: boolean;
  readonly doneBy?: TaskPerson | null | undefined;
  readonly doneAt?: string | null | undefined;
}

/**
 * O checklist da tarefa (design.md §8, Tarefas): uma lista de itens para
 * marcar, com o progresso no alto.
 *
 * - **Acrescentar**: escrever no campo do pé e Enter; o campo fica pronto para
 *   o próximo.
 * - **Marcar**: a caixa à esquerda. O item marcado mostra quem marcou e quando.
 * - **Reescrever**: clicar no texto, que vira campo; Enter ou sair grava, Esc
 *   desiste.
 * - **Tirar**: o × da ponta.
 *
 * Serve aos dois modos do modal: na criação, os itens ainda são da tela e não
 * se marcam (`onAlternar` ausente); na edição, cada gesto grava na hora.
 *
 * Mora numa metade do modal, de altura fixa: o título, o progresso e o campo de
 * acrescentar ficam; só a lista de itens rola. Item novo entra rolando a lista
 * até ele.
 */
export function Checklist({
  itens,
  onAcrescentar,
  onAlternar,
  onReescrever,
  onTirar,
  limite,
}: {
  itens: readonly ItemDoChecklist[];
  onAcrescentar: (texto: string) => void;
  onAlternar?: ((id: string, feito: boolean) => void) | undefined;
  onReescrever: (id: string, texto: string) => void;
  onTirar: (id: string) => void;
  limite: number;
}) {
  const campoId = useId();
  const [novo, setNovo] = useState('');
  const feitos = itens.filter((item) => item.isDone).length;
  const cheio = itens.length >= limite;

  // Item novo entra à vista: a lista rola até o fim quando cresce.
  const listaRef = useRef<HTMLUListElement>(null);
  const quantos = useRef(itens.length);
  useEffect(() => {
    const lista = listaRef.current;
    if (lista && itens.length > quantos.current) lista.scrollTop = lista.scrollHeight;
    quantos.current = itens.length;
  }, [itens.length]);

  function acrescentar(): void {
    const texto = novo.trim();
    if (!texto || cheio) return;
    onAcrescentar(texto.slice(0, 300));
    setNovo('');
  }

  return (
    <section aria-labelledby={`${campoId}-titulo`} className="flex flex-col sm:min-h-0">
      <div className="flex shrink-0 items-baseline justify-between gap-3">
        <h3 id={`${campoId}-titulo`} className="text-[13px] font-medium">
          Checklist
        </h3>
        {itens.length > 0 && onAlternar ? (
          <span className="tabular text-[12px] text-ink-3">
            {feitos} de {itens.length}
          </span>
        ) : null}
      </div>

      {itens.length > 0 && onAlternar ? (
        <span aria-hidden className="mt-2 block h-1 w-full shrink-0 bg-sunken">
          <motion.span
            className="block h-full origin-left bg-success"
            initial={false}
            animate={{ scaleX: itens.length ? feitos / itens.length : 0 }}
            transition={{ duration: 0.4, ease: EASE_OUT_QUINT }}
          />
        </span>
      ) : null}

      {itens.length === 0 ? (
        <p className="mt-2 text-[13px] text-ink-3">
          Nenhum item ainda. Liste os passos da tarefa no campo abaixo.
        </p>
      ) : null}

      <ul ref={listaRef} className="mt-2 flex flex-col sm:min-h-0 sm:overflow-y-auto sm:pr-1">
        <AnimatePresence initial={false}>
          {itens.map((item) => (
            <motion.li
              key={item.id}
              layout="position"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: EASE_OUT_QUINT }}
              className="overflow-hidden"
            >
              <LinhaDoItem
                item={item}
                onAlternar={onAlternar}
                onReescrever={onReescrever}
                onTirar={onTirar}
              />
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>

      <div className="relative mt-1.5 shrink-0">
        <Plus
          size={15}
          strokeWidth={1.75}
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-3"
          aria-hidden
        />
        <input
          id={campoId}
          value={novo}
          disabled={cheio}
          maxLength={300}
          onChange={(evento) => setNovo(evento.target.value)}
          onKeyDown={(evento) => {
            if (evento.key !== 'Enter') return;
            evento.preventDefault();
            acrescentar();
          }}
          onBlur={acrescentar}
          placeholder={cheio ? `Limite de ${limite} itens` : 'Adicionar item e Enter'}
          aria-label="Adicionar item ao checklist"
          className={cn(CONTROL_BASE, CONTROL_TONE.rest, 'h-9 pl-9 text-sm')}
        />
      </div>
    </section>
  );
}

function LinhaDoItem({
  item,
  onAlternar,
  onReescrever,
  onTirar,
}: {
  item: ItemDoChecklist;
  onAlternar?: ((id: string, feito: boolean) => void) | undefined;
  onReescrever: (id: string, texto: string) => void;
  onTirar: (id: string) => void;
}) {
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState(item.content);
  const desistiu = useRef(false);

  function abrir(): void {
    desistiu.current = false;
    setRascunho(item.content);
    setEditando(true);
  }

  function gravar(): void {
    setEditando(false);
    if (desistiu.current) return;
    const texto = rascunho.trim();
    if (texto && texto !== item.content) onReescrever(item.id, texto.slice(0, 300));
  }

  function aoTeclar(evento: KeyboardEvent<HTMLInputElement>): void {
    if (evento.key === 'Enter') {
      evento.preventDefault();
      evento.currentTarget.blur();
    } else if (evento.key === 'Escape') {
      // O Esc é do campo: desiste da edição sem fechar o modal.
      evento.preventDefault();
      evento.stopPropagation();
      desistiu.current = true;
      evento.currentTarget.blur();
    }
  }

  return (
    <div className="group flex min-h-10 items-center gap-2.5 border-b border-line py-1.5 pr-1">
      {onAlternar ? (
        <button
          type="button"
          role="checkbox"
          aria-checked={item.isDone}
          aria-label={item.isDone ? `Desmarcar "${item.content}"` : `Marcar "${item.content}"`}
          onClick={() => onAlternar(item.id, !item.isDone)}
          className="grid size-7 shrink-0 place-items-center"
        >
          <SpringCheck checked={item.isDone} size={18} />
        </button>
      ) : (
        <span aria-hidden className="ml-2 size-1.5 shrink-0 bg-ink-3" />
      )}

      <div className="min-w-0 flex-1">
        {editando ? (
          <input
            // O campo nasce do clique no texto: o foco vai para ele.
            autoFocus
            value={rascunho}
            maxLength={300}
            onChange={(evento) => setRascunho(evento.target.value)}
            onKeyDown={aoTeclar}
            onBlur={gravar}
            aria-label="Texto do item"
            className={cn(CONTROL_BASE, CONTROL_TONE.rest, 'h-8 text-sm')}
          />
        ) : (
          <button
            type="button"
            onClick={abrir}
            title="Clique para reescrever"
            className={cn(
              'block w-full truncate py-1 text-left text-sm transition-colors',
              item.isDone ? 'text-ink-3 line-through decoration-ink-3/60' : 'text-ink',
            )}
          >
            {item.content}
          </button>
        )}
      </div>

      {item.isDone && item.doneBy && !editando ? (
        <span className="hidden shrink-0 text-[12px] text-ink-3 sm:block">
          {item.doneBy.name.split(' ')[0]}
          {item.doneAt ? ` · ${formatDate(item.doneAt).slice(0, 5)}` : ''}
        </span>
      ) : null}

      <button
        type="button"
        onClick={() => onTirar(item.id)}
        aria-label={`Tirar "${item.content}" do checklist`}
        className="grid size-7 shrink-0 place-items-center text-ink-3 opacity-0 transition-[opacity,color] group-hover:opacity-100 group-focus-within:opacity-100 hover:text-danger focus-visible:opacity-100 max-sm:opacity-100"
      >
        <X size={14} strokeWidth={2} aria-hidden />
      </button>
    </div>
  );
}
